/**
 * Horizontaal doorlopende band die reageert op de scrollsnelheid: sneller
 * scrollen versnelt de band, en omhoog scrollen keert de looprichting om.
 *
 * Port van VelocityTrack.tsx. framer-motion leverde daar useVelocity en
 * useSpring; hier is dat een eigen snelheidsmeting met exponentiële demping,
 * wat op hetzelfde gevoel uitkomt zonder de 40 kB bibliotheek.
 *
 * De inhoud staat twee keer in de track, zodat de sprong op -50% samenvalt
 * met het beginpunt van de tweede kopie en de lus onzichtbaar is.
 */

/** Houdt een waarde binnen [min, max) en laat hem daarbinnen rondlopen. */
function wrap(min, max, value) {
  const range = max - min;
  return ((((value - min) % range) + range) % range) + min;
}

class FoleaTrack extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-track]");
    if (!this.track) return;

    this.baseVelocity = Number(this.dataset.velocity ?? 2);
    this.base = 0;
    this.direction = 1;
    this.smoothed = 0;
    this.lastScroll = window.scrollY;
    this.lastFrame = performance.now();
    this.running = false;

    if (this.reducedMotion()) return;

    // Alleen laten lopen als de band in beeld is: een rAF-lus die eeuwig
    // doortelt op een sectie ver buiten beeld is verspilde rekentijd.
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        entry.isIntersecting ? this.start() : this.stop();
      }
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  reducedMotion() {
    return (
      document.documentElement.dataset.animations === "off" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastFrame = performance.now();
    this.lastScroll = window.scrollY;
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    this.running = false;
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  tick = (now) => {
    if (!this.running) return;

    const delta = Math.min(now - this.lastFrame, 64);
    this.lastFrame = now;

    // Scrollsnelheid in pixels per seconde, daarna gedempt. De demping doet
    // wat useSpring({ damping: 50, stiffness: 400 }) in het origineel deed:
    // uitschieters afvlakken zonder merkbare vertraging.
    const scroll = window.scrollY;
    const raw = delta > 0 ? ((scroll - this.lastScroll) / delta) * 1000 : 0;
    this.lastScroll = scroll;
    this.smoothed += (raw - this.smoothed) * 0.18;

    // [0, 1000] px/s wordt [0, 4]. Bewust niet geklemd: hard doorscrollen mag
    // de band echt laten uitschieten.
    const factor = this.smoothed / 250;

    let moveBy = this.direction * this.baseVelocity * (delta / 1000);

    if (factor < 0) this.direction = -1;
    else if (factor > 0) this.direction = 1;

    moveBy += this.direction * moveBy * Math.abs(factor);
    this.base += moveBy;

    this.track.style.transform = `translateX(${wrap(-50, 0, this.base)}%)`;
    this.frame = requestAnimationFrame(this.tick);
  };
}

if (!customElements.get("folea-track")) {
  customElements.define("folea-track", FoleaTrack);
}
